import React, { memo, useEffect, useRef, useState } from 'react';
import { Button, Col, Progress } from 'reactstrap';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import cuid from 'cuid';
import { get } from 'lodash-es';
import { RotateLeftRounded, RotateRightOutlined } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import parseFileName from './parseFileName';
import getLotExtension from './getLotExtension';
import { imagePath, isProd } from '../../../utilities/UrlPaths';
import ImageRotateViewer from '../ImageRotateViewer';

// 10 MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const NO_IMAGE = `${imagePath}/images/NoImage.png`;

// Default Axios requests to retry 3 additional times if they fail
axiosRetry(axios, {
  retries: 3,
  retryDelay: () => 3000
});

/**
 * One uploading file. Addressed by the parent solely through its stable `entry.id`; the parent
 * mounts this component only once the entry is admitted (or rejected), so mount === upload start.
 *
 * Every terminal XHR outcome now settles the wrapping promise (COR-3192): the S3 PUT previously
 * had no `onerror` handler and a non-rejecting `ontimeout`, so a network failure left the promise
 * dangling and the file stuck "uploading" forever. Here load (status-guarded) / error / abort /
 * timeout each resolve or reject exactly once, and the single `catch` maps to one terminal report.
 */
const Step4File = ({
  entry,
  catalogID,
  houseID,
  fileUploadFormat,
  lotNumbers,
  inventoryNumbers,
  filterOnlyErrors,
  handleImageSizeCheck,
  onComplete,
  onError,
  onAbort,
  onDelete,
  onUpdate,
  onClearUpdated
}) => {
  const { id, file, hasError, error } = entry;

  const [progress, setProgress] = useState(0);
  const [imageThumbnail, setImageThumbnail] = useState(null);
  const [imageS3URL, setImageS3URL] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [rotate, setRotate] = useState(0);

  const imageRef = useRef(null);
  const xhrRef = useRef(null);
  const cancelTokenRef = useRef(null);
  const remainingAwsRetriesRef = useRef(5);
  // Full-sequence retries (fresh presign + S3 PUT + CPS PUT) after transient failures.
  const remainingFullRetriesRef = useRef(2);

  // Cancel the in-flight XHR + CPS request (user cancel, or unmount).
  const cancelUpload = () => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel();
    }
    if (xhrRef.current) {
      xhrRef.current.abort();
    }
  };

  /**
   * Wrap the S3 PUT (a raw XHR, for progress + timeout control) in a promise that settles on
   * EVERY terminal outcome. `ontimeout` rejects with 'retry' while retries remain (the caller
   * re-runs) or 'timeout' once exhausted — it never leaves the promise dangling.
   */
  const handlePutRequest = putUrl =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      // Timeout after 5 minutes
      xhr.timeout = 1000 * 60 * 5;
      xhr.open('PUT', putUrl);
      xhr.setRequestHeader('Content-Type', file.type);

      // NOTE: for a PUT this is xhr.upload.onprogress, NOT xhr.onprogress.
      xhr.upload.onprogress = progressEvent => {
        const { loaded, total } = progressEvent;
        setProgress(Math.round((loaded / total) * 100));
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          const err = new Error('default');
          err.status = xhr.status;
          reject(err);
        }
      };

      // Was missing entirely — a network-level failure now rejects instead of hanging.
      xhr.onerror = () => reject(new Error('network'));
      xhr.onabort = () => reject(new Error('abort'));
      xhr.ontimeout = () => {
        if (remainingAwsRetriesRef.current > 0) {
          remainingAwsRetriesRef.current -= 1;
          reject(new Error('retry'));
        } else {
          reject(new Error('timeout'));
        }
      };

      xhr.send(file);
    });

  const uploadFile = async (imageSeq, lotExt, lotNum, invNumber) => {
    try {
      // Safari caches GETs; a unique nocache token keeps each presign request distinct.
      const uniqueIdentifier = cuid();
      const s3BucketUrl = `https://9ctqzg3xxj.execute-api.us-east-1.amazonaws.com/${
        isProd ? 'prod' : 'stage'
      }/INV-ImageUploader?numImages=0&bucket=/house/${houseID}/catalog/${catalogID}&nocache=${uniqueIdentifier}`;

      const awsUrlResponse = await axios(s3BucketUrl, {
        'axios-retry': { retries: 3, retryDelay: () => 3000 }
      });

      const imageURL = get(awsUrlResponse, 'data[0].imageUrl', '');
      const awsPutUrl = get(awsUrlResponse, 'data[0].putUrl', '');

      // Step 2 - PUT the file to S3
      await handlePutRequest(awsPutUrl);

      // Step 3 - PUT to CPS
      const cpsPutUrl = `/cps/app/image/house/${houseID}/catalog/${catalogID}?fromDIY=true&doValidation=false`;
      const cpsPutData =
        fileUploadFormat === 'lotNumber'
          ? [{ imageSeq, imageURL, lotExt, lotNum }]
          : [{ imageSeq, imageURL, lotExt, invNumber }];

      cancelTokenRef.current = axios.CancelToken.source();

      const cpsPutResponse = await axios.put(cpsPutUrl, cpsPutData, {
        cancelToken: cancelTokenRef.current.token,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      });

      const cpsPutResponseError = get(cpsPutResponse, 'data.data.images[0].error', null);
      if (!cpsPutResponseError) {
        const s3Url = get(cpsPutResponse, 'data.data.images[0].imageS3URL', null);
        const photoID = get(cpsPutResponse, 'data.data.images[0].photoID', null);
        setImageS3URL(s3Url);
        setIsUploading(false);
        onComplete(id, photoID);
      } else if (cpsPutResponseError === 'Invalid Image') {
        onError(id, 'invalidImage');
        setIsUploading(false);
      } else if (remainingFullRetriesRef.current > 0) {
        // Transient CPS error — retry the full sequence with a fresh presign URL.
        remainingFullRetriesRef.current -= 1;
        setTimeout(() => uploadFile(imageSeq, lotExt, lotNum, invNumber), 3000);
      } else {
        onError(id, 'timeout');
        setIsUploading(false);
      }
    } catch (e) {
      if (axios.isCancel(e) || e.message === 'abort') {
        // Withdrawn/cancelled — remove the file, no error to surface.
        onAbort(id);
        return;
      }
      if (e.message === 'retry') {
        // XHR timeout with S3 retries remaining — re-run after the standard delay.
        setTimeout(() => uploadFile(imageSeq, lotExt, lotNum, invNumber), 3000);
        return;
      }
      // Network error or exhausted S3 retries — retry the full sequence (fresh presign) before failing.
      if (!e.status && remainingFullRetriesRef.current > 0) {
        remainingFullRetriesRef.current -= 1;
        setTimeout(() => uploadFile(imageSeq, lotExt, lotNum, invNumber), 3000);
        return;
      }
      // A network error / axios failure sends no status — treat as a connection loss.
      let errorMsg = e.message;
      if (!e.status) {
        errorMsg = 'timeout';
      }
      onError(id, errorMsg);
      setIsUploading(false);
    }
  };

  const uploadNewFile = () => {
    // Rejected-at-dropzone files never upload.
    if (hasError) {
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      onError(id, 'size');
      setIsUploading(false);
      return;
    }

    // Small-image check — allows sub-1000px images but flags the warning banner.
    try {
      const sizeTest = new Image();
      sizeTest.src = URL.createObjectURL(file);
      sizeTest.onload = () => {
        const isSmall = sizeTest.naturalWidth < 1000 || sizeTest.naturalHeight < 1000;
        handleImageSizeCheck(isSmall);
      };
    } catch (e) {
      // Non-fatal: a failed dimension probe should not block the upload.
      console.log(e); // eslint-disable-line no-console
    }

    const thumbnail = URL.createObjectURL(file);
    setIsUploading(true);
    setImageThumbnail(thumbnail);

    // Match the file to a lot / inventory number.
    const parsedFileName = parseFileName(file.name);
    const splitFile = parsedFileName.split('.');
    const lotOrInventoryNumber = splitFile[0];

    const imageSeq = splitFile.length >= 3 ? splitFile[1] : '0';
    if (!/^\d+$/.test(imageSeq)) {
      onError(id, 'imageSeq');
      setIsUploading(false);
      return;
    }

    let lotNum = '';
    let lotExt = '';
    let invNumber = '';
    if (fileUploadFormat === 'lotNumber') {
      if (!lotNumbers.has(lotOrInventoryNumber)) {
        onError(id, 'lotNum');
        setIsUploading(false);
        return;
      }
      const parseLotName = getLotExtension(lotOrInventoryNumber);
      lotNum = parseLotName.lotNumber;
      lotExt = parseLotName.lotExt;
    } else if (fileUploadFormat === 'inventoryNumber') {
      const lotIndex = inventoryNumbers.findIndex(
        inv => inv.invNumber === lotOrInventoryNumber
      );
      if (lotIndex < 0) {
        onError(id, 'invNum');
        setIsUploading(false);
        return;
      }
      lotNum = inventoryNumbers[lotIndex].lotNumber;
      lotExt = inventoryNumbers[lotIndex].lotExt;
      invNumber = inventoryNumbers[lotIndex].invNumber;
    }

    uploadFile(imageSeq, lotExt, lotNum, invNumber);
  };

  // Upload once the entry is admitted. Abort any in-flight request on unmount.
  useEffect(() => {
    if (!entry.admitted) return;
    uploadNewFile();
    return () => cancelUpload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.admitted]);

  // Edit/replace re-upload: fires only when the entry is re-armed via UPDATE.
  useEffect(() => {
    if (entry.isUpdatedFile) {
      onClearUpdated(id);
      remainingAwsRetriesRef.current = 5;
      remainingFullRetriesRef.current = 2;
      uploadNewFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.isUpdatedFile]);

  const handleRotateChange = isHorizontal => {
    if (isHorizontal) {
      if (rotate === 270) setRotate(0);
      else if (rotate >= -270 && rotate < 270) setRotate(rotate + 90);
    } else if (rotate === -270) setRotate(0);
    else if (rotate > -270 && rotate <= 270) setRotate(rotate - 90);
  };

  const saveImageHandler = () => {
    const canvas = imageRef.current;
    if (!canvas) {
      return;
    }
    canvas.toBlob(blob => {
      if (!blob) {
        return;
      }
      const updatedFile = new File([blob], file.name, { type: file.type });
      setImageS3URL(null);
      setIsUploading(true);
      setProgress(0);
      setRotate(0);
      onUpdate(updatedFile, id);
    }, file.type);
  };

  if (filterOnlyErrors && !hasError) {
    return null;
  }

  let imageSrc;
  const errorCode = get(error, 'code', null);
  if (errorCode === 'type' || errorCode === 'timeout') {
    // Invalid type / timeout: skip holding a thumbnail in memory, show the placeholder.
    imageSrc = NO_IMAGE;
  } else if (imageS3URL) {
    imageSrc = imageS3URL;
  } else {
    imageSrc = imageThumbnail || NO_IMAGE;
  }

  let cancelDeleteOnClick;
  if (progress === 0) {
    cancelDeleteOnClick = () => onAbort(id);
  } else if (isUploading) {
    cancelDeleteOnClick = () => cancelUpload();
  } else {
    cancelDeleteOnClick = () => onDelete(id);
  }

  return (
    <Col xs={3} className="file-container py-3">
      <div className={hasError ? 'border-invalid px-3 pt-2' : 'file-border px-3 pt-2'}>
        <div className="d-flex">{file.name}</div>
        <div className="thumb-container d-flex justify-content-center align-items-center mt-2 mb-3">
          {imageSrc && (
            <ImageRotateViewer
              ref={imageRef}
              rotate={rotate}
              className={
                isUploading ? 'w-100 mh-100' : `w-100 mh-100 ${rotate ? '' : 'opacity-50'}`
              }
              file={rotate ? file : undefined}
              src={imageSrc}
              alt="No Thumbnail"
            />
          )}
          {!rotate && !isUploading && progress > 0 && !hasError && (
            <FontAwesomeIcon
              icon="check-circle"
              size="3x"
              className="position-absolute color-success"
            />
          )}
          {!isUploading && hasError && (
            <FontAwesomeIcon
              icon="exclamation-triangle"
              size="3x"
              className="position-absolute color-danger"
            />
          )}
        </div>
        {isUploading && <Progress className="mb-2" value={progress} />}
        <div id="file-actions" className={!isUploading ? 'mb-2' : ''}>
          <Button className="mt-0" color="link" onClick={cancelDeleteOnClick}>
            {(isUploading || progress === 0) && !hasError ? 'Cancel upload' : 'Delete'}
          </Button>
          {!isUploading && progress !== 0 && !hasError && (
            <>
              {!!rotate && (
                <Button className="mt-0 ml-2" color="link" onClick={saveImageHandler}>
                  Save
                </Button>
              )}
              <div id="action-arrows">
                <RotateRightOutlined fontSize="large" onClick={() => handleRotateChange(true)} />
                <RotateLeftRounded fontSize="large" ml={2} onClick={() => handleRotateChange(false)} />
              </div>
            </>
          )}
        </div>
      </div>
      {hasError && <div className="text-danger my-2">{error.description}</div>}
    </Col>
  );
};

/**
 * Shallow-prop memo. The reducer preserves the object reference of unchanged entries, and the
 * parent passes stable (useCallback / dispatch-derived) callbacks — so completing one file does
 * not re-render its siblings, preserving the large-catalog performance the old shouldComponentUpdate
 * provided.
 */
export default memo(Step4File);
